import pino from 'pino';
import { config } from '../next-logger.config';
import redactor from './redactor';
import isEnvDevelopment from '../lib/isEnvDevelopment';

const LOGGER_NAME_KEY = 'nw_logger_name';

const BASE_LOGGER = pino(config);

export class Logger {
  private _pinoLogger: pino.Logger;
  // Whether to log the message. Defaults to true.
  private _isLoggingEnabled: boolean = true;

  constructor(pinoLogger: pino.Logger) {
    this._pinoLogger = pinoLogger;
  }

  public static logger(options?: { tags?: Record<string, string> }): Logger {
    const pinoLogger = BASE_LOGGER.child({ ...options?.tags });
    return new Logger(pinoLogger);
  }

  public setIsLoggingEnabled(isLoggingEnabled: boolean): void {
    this._isLoggingEnabled = isLoggingEnabled;
  }

  private static formatMessage({
    loggerName,
    message,
    context,
  }: {
    loggerName: string;
    message: string;
    context: Record<string, any> | null;
  }): Record<string, any> {
    return {
      [LOGGER_NAME_KEY]: isEnvDevelopment()
        ? loggerName
        : redactor.apply(loggerName),
      message: isEnvDevelopment() ? message : redactor.apply(message),
      ...(context && {
        context: isEnvDevelopment() ? context : redactor.apply(context),
      }),
    };
  }

  public error(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null
  ): void {
    if (!this._isLoggingEnabled) return;
    this._pinoLogger.error(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }

  public warn(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null
  ): void {
    if (!this._isLoggingEnabled) return;
    this._pinoLogger.warn(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }

  public info(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null
  ): void {
    if (!this._isLoggingEnabled) return;
    this._pinoLogger.info(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }

  public debug(
    loggerName: string,
    message: string,
    context: Record<string, any> | null = null,
    forceLog: boolean = false
  ): void {
    if (!forceLog && !this._isLoggingEnabled) return;
    this._pinoLogger.debug(
      Logger.formatMessage({
        loggerName,
        message,
        context,
      })
    );
  }
}
